import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { 
  Save, RefreshCw, Image, Video, Upload, Play, Eye, Home, Volume2, VolumeX, Maximize
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const CMSTeamPageConfigPage = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await api.get('/cms/team-page-config');
      setConfig(res.data);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/cms/team-page-config', config);
      toast.success('Configuration enregistrée');
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'team-page');

    try {
      const res = await api.post('/cms/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setConfig({ ...config, [field]: res.data.url });
      toast.success('Image téléchargée');
    } catch (error) {
      toast.error('Erreur de téléchargement');
    } finally {
      setUploading(false);
    }
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (match) return `https://www.youtube.com/embed/${match[1]}`;
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Configuration Page Équipe</h1>
          <p className="text-slate-500">
            Personnalisez l'en-tête et la vidéo de présentation de la page équipe
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-gradient-to-r from-cyan-500 to-blue-600">
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Hero Text */}
        <Card>
          <CardHeader>
            <CardTitle>Texte de l'en-tête</CardTitle>
            <CardDescription>Titre et sous-titre affichés sur la page</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Titre principal</Label>
              <Input
                value={config?.hero_title || ''}
                onChange={(e) => setConfig({ ...config, hero_title: e.target.value })}
                placeholder="Des créatifs complémentaires"
              />
            </div>

            <div className="space-y-2">
              <Label>Sous-titre</Label>
              <Input
                value={config?.hero_subtitle || ''}
                onChange={(e) => setConfig({ ...config, hero_subtitle: e.target.value })}
                placeholder="Une équipe de passionnés à votre service"
              />
            </div>
          </CardContent>
        </Card>

        {/* Video Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Play className="w-5 h-5 text-cyan-500" />
              Vidéo de présentation
            </CardTitle>
            <CardDescription>Vidéo YouTube affichée dans la section "Découvrez notre équipe"</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>URL YouTube</Label>
              <Input
                value={config?.video_url || ''}
                onChange={(e) => setConfig({ ...config, video_url: e.target.value })}
                placeholder="https://www.youtube.com/watch?v=..."
              />
              <p className="text-xs text-slate-500">
                Formats acceptés : youtube.com/watch?v=... ou youtu.be/...
              </p>
            </div>

            <div className="space-y-2">
              <Label>Miniature (optionnel)</Label>
              <div className="flex gap-2">
                <Input
                  value={config?.video_thumbnail || ''}
                  onChange={(e) => setConfig({ ...config, video_thumbnail: e.target.value })}
                  placeholder="URL de l'image miniature"
                  className="flex-1"
                />
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleUpload(e, 'video_thumbnail')}
                  />
                  <Button type="button" variant="outline" disabled={uploading}>
                    <Upload className="w-4 h-4" />
                  </Button>
                </label>
              </div>
            </div>

            {/* Homepage Video Options */}
            <div className="p-4 bg-cyan-50 rounded-lg border border-cyan-200 space-y-4">
              <div className="flex items-center gap-2 text-cyan-800 font-medium">
                <Home className="w-4 h-4" />
                Affichage sur la page d&apos;accueil
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium">Afficher sur la page d&apos;accueil</Label>
                  <p className="text-xs text-slate-500">Affiche une vignette cliquable dans la section équipe</p>
                </div>
                <Switch
                  checked={config?.show_on_homepage === true}
                  onCheckedChange={(checked) => setConfig({ ...config, show_on_homepage: checked })}
                />
              </div>

              {config?.show_on_homepage && (
                <>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <VolumeX className="w-4 h-4" />
                        Autoplay en muet
                      </Label>
                      <p className="text-xs text-slate-500">La vidéo joue automatiquement sans son</p>
                    </div>
                    <Switch
                      checked={config?.autoplay_muted === true}
                      onCheckedChange={(checked) => setConfig({ ...config, autoplay_muted: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <Maximize className="w-4 h-4" />
                        Plein écran au clic
                      </Label>
                      <p className="text-xs text-slate-500">Ouvre la vidéo en plein écran avec son</p>
                    </div>
                    <Switch
                      checked={config?.fullscreen_on_click !== false}
                      onCheckedChange={(checked) => setConfig({ ...config, fullscreen_on_click: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-sm font-medium">Lecture en boucle</Label>
                      <p className="text-xs text-slate-500">La vidéo redémarre automatiquement</p>
                    </div>
                    <Switch
                      checked={config?.loop_video === true}
                      onCheckedChange={(checked) => setConfig({ ...config, loop_video: checked })}
                    />
                  </div>
                </>
              )}
            </div>

            {/* Video Preview */}
            {config?.video_url && getYouTubeEmbedUrl(config.video_url) && (
              <div className="aspect-video rounded-lg overflow-hidden bg-slate-100">
                <iframe
                  src={getYouTubeEmbedUrl(config.video_url)}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="Team video preview"
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Background Configuration */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Image className="w-5 h-5 text-cyan-500" />
              Arrière-plan de l'en-tête
            </CardTitle>
            <CardDescription>Image ou vidéo de fond pour la section héro de la page équipe</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Background Type */}
            <div className="space-y-2">
              <Label>Type d'arrière-plan</Label>
              <div className="flex gap-2">
                <Button
                  variant={config?.background_type === 'image' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setConfig({ ...config, background_type: 'image' })}
                >
                  <Image className="w-4 h-4 mr-2" /> Image
                </Button>
                <Button
                  variant={config?.background_type === 'video' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setConfig({ ...config, background_type: 'video' })}
                >
                  <Video className="w-4 h-4 mr-2" /> Vidéo YouTube
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{config?.background_type === 'video' ? 'URL YouTube du fond' : 'URL de l\'image de fond'}</Label>
              <div className="flex gap-2">
                <Input
                  value={config?.background_image || ''}
                  onChange={(e) => setConfig({ ...config, background_image: e.target.value })}
                  placeholder={config?.background_type === 'video' ? 'https://youtube.com/watch?v=...' : 'https://...'}
                  className="flex-1"
                />
                {config?.background_type === 'image' && (
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleUpload(e, 'background_image')}
                    />
                    <Button type="button" variant="outline" disabled={uploading}>
                      {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    </Button>
                  </label>
                )}
              </div>
            </div>

            {/* Background Preview */}
            <div className="space-y-2">
              <Label>Aperçu</Label>
              <div className="aspect-[21/9] rounded-lg overflow-hidden bg-slate-100 relative">
                {config?.background_type === 'video' && config?.background_image ? (
                  <iframe
                    src={`${getYouTubeEmbedUrl(config.background_image)}?autoplay=0&mute=1`}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    title="Background preview"
                  />
                ) : config?.background_image ? (
                  <img 
                    src={config.background_image.startsWith('http') ? config.background_image : `${process.env.REACT_APP_BACKEND_URL}${config.background_image}`}
                    alt="Background preview" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-slate-400">
                    <Eye className="w-8 h-8 mr-2" />
                    Aucun arrière-plan configuré
                  </div>
                )}
                
                {/* Overlay preview */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#1a365d]/90 to-[#2d4a7c]/90 flex items-center justify-center">
                  <div className="text-center text-white">
                    <h2 className="text-2xl font-bold">{config?.hero_title || 'Titre'}</h2>
                    <p className="text-white/80">{config?.hero_subtitle || 'Sous-titre'}</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CMSTeamPageConfigPage;
